import { useState } from 'react'

import { Box, Button, Icon, Link, Text } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import { InvoiceReconciliationQuery } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI, dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { LineItem } from 'modules/banking/components/NewReconciliationForm/ReconciliationSuggestions'
import { useReconciliationSuggestion } from 'modules/banking/hooks/useReconciliationSuggestions'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

import { Carousel } from './Carousel'
import { CarouselFooter } from './CarouselFooter'

type Props = {
  invoice: InvoiceReconciliationQuery['finance']['financialDocumentsConnection']['nodes'][0]
  onHideSuggestions?: () => void
}

export const ReconciliationInvoiceSuggestions = ({ invoice, onHideSuggestions }: Props) => {
  const [showSuggestion, setShowSuggestion] = useState(false)
  const { navigation } = useNavigation()
  const { access, company } = useCurrentSession()
  const [currentIndex, setCurrentIndex] = useState(0)

  const suggestions = invoice.reconciliationSuggestions
  const transaction = suggestions[0]?.transaction

  const { isLoading, firstSuggestion, confirmSuggestion, rejectSuggestion } =
    useReconciliationSuggestion({
      suggestion: suggestions[0],
      companyId: company.id,
    })

  setTimeout(() => {
    setShowSuggestion(true)
  }, 100)

  const handleReject = useUnsafeCallback(() => {
    setCurrentIndex((curr) => (curr > 0 ? curr - 1 : 0))
    rejectSuggestion()
  }, [rejectSuggestion])

  const actionButtons = useUnsafeMemo(
    () => (
      <Box flexDirection="row" gap="s6">
        <Button type="secondary" onClick={handleReject} disabled={isLoading}>
          {i18n.t('banking.reconciliations.suggestions.reject')}
        </Button>
        <Button onClick={confirmSuggestion} disabled={isLoading}>
          {i18n.t('banking.reconciliations.suggestions.confirm')}
        </Button>
      </Box>
    ),
    [confirmSuggestion, isLoading, handleReject]
  )

  if (!suggestions[0] || !transaction || !showSuggestion) return null

  const locale = access.locale

  const contentAnimationVariants = {
    enter: () => {
      if (firstSuggestion) {
        return {
          opacity: 0,
          translateY: '-100%',
        }
      } else {
        return {
          opacity: 0,
          translateY: 1,
        }
      }
    },
    show: {
      opacity: 1,
      translateY: 0,
      translateX: 0,
    },
  }

  return (
    <Box paddingBottom="s16" gap="s16" flexDirection="row" justifyContent="spaceBetween">
      <Box width="full">
        <Box flexDirection="row" width="full" alignItems="center" justifyContent="spaceBetween">
          <Box flexDirection="row" gap="s4">
            <Text.Normal weight="semibold">
              {i18n.t('banking.reconciliations.suggestions.title')}
            </Text.Normal>
            <Icon.Medium icon="ai" color="grey800" />
          </Box>
          {onHideSuggestions && (
            <Link.Touchable onClick={onHideSuggestions}>
              {i18n.t('banking.reconciliations.suggestions.dismiss')}
            </Link.Touchable>
          )}
        </Box>
        <Text.Normal>{i18n.t('banking.reconciliations.suggestions.subtitle_invoice')}</Text.Normal>
      </Box>

      <Box width="full" overflowY="hidden">
        <Box
          background="white"
          paddingX="s24"
          paddingY="s16"
          flexDirection="row"
          gap="s32"
          style={{
            border: '1px solid #e2e2e5',
            borderRadius: '16px',
            zIndex: '99',
          }}
        >
          <Box>
            <Text.Normal color="grey800" size="200">
              {invoice.documentDate && dateBackendToUI(invoice.documentDate)}
            </Text.Normal>
            <Text.Normal color="grey800" size="200">
              {formattedAmount({
                amountCents: Number(invoice.totalAmountCents),
                currency: invoice.currency || '',
                locale,
              })}
            </Text.Normal>
          </Box>
          <Box>
            <Text.Normal color="grey1000" size="200" weight="medium">
              {invoice.documentNumber || ''}
            </Text.Normal>
            <Text.Normal color="grey800" size="200">
              {contactFullName(invoice.vendor)}
            </Text.Normal>
          </Box>
        </Box>
        <AnimatePresence mode="popLayout">
          <Box.Motion
            flexGrow
            paddingTop="s0"
            initial="enter"
            animate="show"
            exit="exit"
            variants={contentAnimationVariants}
            transition={{ easing: 'easeInOut', duration: 0.5 }}
            maxHeight="full"
          >
            <Box
              gap="s16"
              background="grey100"
              style={{
                border: '1px solid #e2e2e5',
                borderBottomLeftRadius: '16px',
                borderBottomRightRadius: '16px',
                borderTop: 'none',
                marginTop: '-15px',
                padding: '32px 24px 16px 24px',
              }}
            >
              <Carousel
                index={currentIndex}
                footer={
                  <CarouselFooter
                    total={suggestions.length}
                    currentIndex={currentIndex}
                    setCurrentIndex={setCurrentIndex}
                    actionButtonsComponent={actionButtons}
                  />
                }
              >
                {suggestions.map((suggestion) => (
                  <Box key={suggestion.id} gap="s12">
                    <Box justifyContent="spaceBetween" flexDirection="row">
                      <LineItem
                        label={i18n.t('banking.reconciliations.suggestions.description')}
                        value={suggestion.transaction.description || ''}
                        textAlign="left"
                      />
                    </Box>
                    <Box justifyContent="spaceBetween" flexDirection="row">
                      <LineItem
                        label={i18n.t('banking.reconciliations.suggestions.date')}
                        value={dateBackendDateTimeToUI(suggestion.transaction.bookingDate)}
                        textAlign="left"
                      />
                      <LineItem
                        label={i18n.t('banking.reconciliations.suggestions.amount')}
                        value={formattedAmount({
                          amountCents: Number(suggestion.transaction.amountCents),
                          currency: suggestion.transaction.currency || '',
                          locale,
                        })}
                        textAlign="right"
                      />
                    </Box>
                    <Box
                      aria-label={i18n.t('banking.reconciliations.suggestions.view_invoice')}
                      flexDirection="row"
                      alignItems="center"
                      gap="s4"
                    >
                      <Link.Router
                        to={navigation.finance.banking.accounts.accountId.transactions.transactionId.fullPath(
                          {
                            id: suggestion.transaction.bankAccount.id,
                            transaction_id: suggestion.transaction.id,
                          }
                        )}
                      >
                        <Text.Normal color="grey800" size="200" weight="medium">
                          {i18n.t('banking.reconciliations.suggestions.view_transaction')}
                        </Text.Normal>
                      </Link.Router>
                      <Icon.Small icon="arrow-right" color="grey800" />
                    </Box>
                  </Box>
                ))}
              </Carousel>
            </Box>
          </Box.Motion>
        </AnimatePresence>
      </Box>
    </Box>
  )
}
